import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { StylesConfig } from 'react-select';
import { useService } from 'react-service-locator';
import { DiseaseService } from '../../services/disease.service';
import { SearchService } from '../../services/search.service';
import './DiseaseSymptomInput.scss';

type OptionType = { label: string; value: string; disabled: boolean };

const customSelectStyles: StylesConfig<{ label: string; value: string; disabled: boolean }> = {
    control: (base, state) => ({
        ...base,
        minHeight: 48,
        height: 48,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: state.isDisabled ? '#D7DCE8' : state.isFocused ? '#3A58A1' : '#B9C3DA',
        backgroundColor: state.isDisabled ? '#E9EDF6' : '#FFFFFF',
        boxShadow: 'none',
        ':hover': { borderColor: state.isDisabled ? '#D7DCE8' : '#3A58A1' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 12px' }),
    placeholder: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#A7B1C7' : '#8E99B7',
        fontWeight: 500,
    }),
    singleValue: (base) => ({ ...base, color: '#0B2566', fontWeight: 600 }),
    input: (base) => ({ ...base, color: '#0B2566', fontWeight: 600, margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        paddingRight: 12,
        paddingLeft: 4,
        color: state.isDisabled ? '#A7B1C7' : '#3A58A1',
    }),
    menu: (base) => ({ ...base, zIndex: 5000, borderRadius: 8, overflow: 'hidden' }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#EDF1FA' : state.isFocused ? '#F4F6FB' : 'transparent',
        color: state.isDisabled ? '#8E99B7' : '#0B2566',
        fontWeight: state.isDisabled ? 500 : 600,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
};

const diseaseDisabledOptions: OptionType[] = [
    { label: 'Other diseases (to be implemented)', value: 'andere aandoening', disabled: true },
];

const DiseaseSymptomInput: FC = () => {
    const searchService = useService(SearchService);
    const diseaseService = useService(DiseaseService);
    const selectDisease = (disease?: string) => {
        searchService.setDisease(disease);
        searchService.setSymptoms([]);
    };
    const { t } = useTranslation();
    useEffect(() => {
        diseaseService.fetchDiseases();
    }, []);
    return (
        <div className="DiseaseSymptomInput">
            <div>
                <p className="label body body-normal">{t('pages.search-page.disease')}</p>
                <Select
                    className="body body-normal"
                    value={
                        searchService.state.disease
                            ? {
                                  label: searchService.state.disease,
                                  value: searchService.state.disease,
                                  disabled: false,
                              }
                            : undefined
                    }
                    isMulti={false}
                    options={[
                        ...diseaseService.state.diseases.map((d) => ({ label: d, value: d, disabled: false })),
                        ...diseaseDisabledOptions,
                    ]}
                    onChange={(v) => selectDisease(v?.value)}
                    isOptionDisabled={(v: OptionType) => v.disabled}
                    isClearable={true}
                    placeholder={t('components.search-disease.select')}
                    styles={customSelectStyles}
                />
            </div>
        </div>
    );
};

export default DiseaseSymptomInput;
